// components/MediaGrid.tsx

'use client'

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import ColorScheme from "./colorScheme";
import CustomPortableText from "@/components/portable-text";
import { PortableTextBlock } from "next-sanity";
import Image from "next/image";

type MediaGridProps =
  | Extract<
      NonNullable<NonNullable<HomepageQueryResult>["content"]>[number],
      { _type: "mediaGrid" }
    >
  | Extract<
      NonNullable<NonNullable<PageQueryResult>["content"]>[number],
      { _type: "mediaGrid" }
    >;


// 👇 Tell TS what a block actually looks like
type VideoBlock = {
  _type: "videoBlock";
  url?: string | null;
  caption?: string | null;
};

type ImageBlock = {
  _type: "imageBlock"; // or whatever your schema uses for the image type
  assetPath?: string | null;
  caption?: string | null;
};


type MediaBlock = VideoBlock | ImageBlock;



export default function MediaGrid(props: MediaGridProps) {
  const { heading, content, mediaBlocks: rawMediaBlocks, colorScheme } = props;

  const mediaBlocks = (rawMediaBlocks ?? []) as MediaBlock[];


  return (
    <section className="media-grid">
      <ColorScheme colorScheme={colorScheme && colorScheme != 'default'? colorScheme : 'plum'}>
        <div className="bg-(--color-background) text-(--color-foreground)">
          <div className="p-5 md:p-7.5 marketing-container md:flex md:flex-row-reverse md:gap-7.5">
            <div className="md:flex-1 grid grid-cols-4 grid-rows-4 border relative">
              {[...Array(16)].map((_, i) =>
                <div key={i} className="border aspect-square"></div>
              )}
              {mediaBlocks && mediaBlocks[0] &&
                <div className="mediaGridAnimation0 w-[calc(25%-2px)] ml-px h-[calc(25%-2px)] mt-px absolute top-1/2 left-1/4">
                  {mediaBlocks[0]._type == "videoBlock"?
                    mediaBlocks[0].url &&
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={mediaBlocks[0].url}
                        className="absolute inset-0 object-cover w-full h-full"
                      />
                    :
                    mediaBlocks[0].assetPath &&
                      <Image
                        src={mediaBlocks[0].assetPath}
                        alt={mediaBlocks[0].caption || ""}
                        fill={true}
                        loading="lazy"
                        sizes="(min-width: 1024px) 25vw, 50vw"
                        className="object-cover"
                      />
                  }
                </div>
              }
              {mediaBlocks && mediaBlocks[1] &&
                <div className="mediaGridAnimation1 w-[calc(25%-2px)] ml-px h-[calc(25%-2px)] mt-px absolute top-0 left-1/2">
                  {mediaBlocks[1]._type == "videoBlock"?
                    mediaBlocks[1].url &&
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={mediaBlocks[1].url}
                        className="absolute inset-0 object-cover w-full h-full"
                      />
                    :
                    mediaBlocks[1].assetPath &&
                      <Image
                        src={mediaBlocks[1].assetPath}
                        alt={mediaBlocks[1].caption || ""}
                        fill={true}
                        loading="lazy"
                        sizes="(min-width: 1024px) 25vw, 50vw"
                        className="object-cover"
                      />
                  }
                </div>
              }
              {mediaBlocks && mediaBlocks[2] &&
                <div className="mediaGridAnimation2 w-[calc(25%-2px)] ml-px h-[calc(25%-2px)] mt-px absolute top-1/4 left-3/4">
                  {mediaBlocks[2]._type == "videoBlock"?
                    mediaBlocks[2].url &&
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={mediaBlocks[2].url}
                        className="absolute inset-0 object-cover w-full h-full"
                      />
                    :
                    mediaBlocks[2].assetPath &&
                      <Image
                        src={mediaBlocks[2].assetPath}
                        alt={mediaBlocks[2].caption || ""}
                        fill={true}
                        loading="lazy"
                        sizes="(min-width: 1024px) 25vw, 50vw"
                        className="object-cover"
                      />
                  }
                </div>
              }
              {mediaBlocks && mediaBlocks[3] &&
                <div className="mediaGridAnimation3 w-[calc(25%-2px)] ml-px h-[calc(25%-2px)] mt-px absolute top-3/4 left-0">
                  {mediaBlocks[3]._type == "videoBlock"?
                    mediaBlocks[3].url &&
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={mediaBlocks[3].url}
                        className="absolute inset-0 object-cover w-full h-full"
                      />
                    :
                    mediaBlocks[3].assetPath &&
                      <Image
                        src={mediaBlocks[3].assetPath}
                        alt={mediaBlocks[3].caption || ""}
                        fill={true}
                        loading="lazy"
                        sizes="(min-width: 1024px) 25vw, 50vw"
                        className="object-cover"
                      />
                  }
                </div>
              }
            </div>
            <div className="flex flex-col gap-15 md:flex-1">
              {heading && <h2 className="max-md:mt-10 md:max-w-131 text-box-trim-both">{heading}</h2>}
              {content && <CustomPortableText className="max-md:mb-16.25 md:max-w-135.5" value={content as PortableTextBlock[]} />}
            </div>
          </div>
        </div>
      </ColorScheme>
    </section>
  )
}